/**
 * Goal Bingo API client
 *
 * Provides methods for interacting with the Goal Bingo Service API.
 * Goal Bingo is a gamified goal-tracking feature using bingo card format.
 */

import { fetchApi, buildQueryString } from './client';
import type {
  BingoCard,
  BingoCell,
  BingoProgress,
  BingoCardListResponse,
  BingoCellListResponse,
  BingoPatternListResponse,
  CreateBingoCardInput,
  UpdateBingoCardInput,
  CreateBingoCellInput,
  UpdateBingoCellInput,
  BingoCardFilters,
} from '@/types/bingo';

// Goal Bingo API base URL - proxied through BFF
const BINGO_API_BASE = import.meta.env.VITE_BINGO_API_BASE ?? '/api';

/**
 * Get individual ID from auth context or use default
 * In production, this should come from the auth context
 */
function getIndividualId(): string {
  // TODO: Get from auth context
  return 'default-user';
}

/**
 * List bingo cards with optional filters
 */
export async function listBingoCards(
  filters?: BingoCardFilters
): Promise<BingoCardListResponse> {
  const queryParams: Record<string, string | number> = {
    individual_id: getIndividualId(),
  };
  
  if (filters?.year) queryParams.year = filters.year;
  if (filters?.status) queryParams.status = filters.status;

  const query = buildQueryString(queryParams);
  return fetchApi<BingoCardListResponse>(`${BINGO_API_BASE}/bingo-cards${query}`);
}

/**
 * Get a single bingo card by ID
 */
export async function getBingoCard(cardId: string): Promise<BingoCard> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<BingoCard>(`${BINGO_API_BASE}/bingo-cards/${cardId}${query}`);
}

/**
 * Create a new bingo card
 */
export async function createBingoCard(input: CreateBingoCardInput): Promise<BingoCard> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<BingoCard>(`${BINGO_API_BASE}/bingo-cards${query}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Update a bingo card
 */
export async function updateBingoCard(
  cardId: string,
  input: UpdateBingoCardInput
): Promise<BingoCard> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<BingoCard>(`${BINGO_API_BASE}/bingo-cards/${cardId}${query}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * Archive a bingo card (soft delete)
 */
export async function deleteBingoCard(cardId: string): Promise<{ message: string }> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<{ message: string }>(`${BINGO_API_BASE}/bingo-cards/${cardId}${query}`, {
    method: 'DELETE',
  });
}

/**
 * List cells for a bingo card
 */
export async function listBingoCells(
  cardId: string,
  status?: string
): Promise<BingoCellListResponse> {
  const queryParams: Record<string, string> = {
    individual_id: getIndividualId(),
  };
  if (status) queryParams.status = status;

  const query = buildQueryString(queryParams);
  return fetchApi<BingoCellListResponse>(`${BINGO_API_BASE}/bingo-cards/${cardId}/cells${query}`);
}

/**
 * Get a single cell by ID
 */
export async function getBingoCell(cardId: string, cellId: string): Promise<BingoCell> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<BingoCell>(`${BINGO_API_BASE}/bingo-cards/${cardId}/cells/${cellId}${query}`);
}

/**
 * Create a new cell
 */
export async function createBingoCell(
  cardId: string,
  input: CreateBingoCellInput
): Promise<BingoCell> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<BingoCell>(`${BINGO_API_BASE}/bingo-cards/${cardId}/cells${query}`, {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Update a cell
 */
export async function updateBingoCell(
  cardId: string,
  cellId: string,
  input: UpdateBingoCellInput
): Promise<BingoCell> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<BingoCell>(`${BINGO_API_BASE}/bingo-cards/${cardId}/cells/${cellId}${query}`, {
    method: 'PUT',
    body: JSON.stringify(input),
  });
}

/**
 * Delete a cell
 */
export async function deleteBingoCell(
  cardId: string,
  cellId: string
): Promise<{ message: string }> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<{ message: string }>(
    `${BINGO_API_BASE}/bingo-cards/${cardId}/cells/${cellId}${query}`,
    { method: 'DELETE' }
  );
}

/**
 * Mark a cell as complete
 */
export async function completeBingoCell(
  cardId: string,
  cellId: string
): Promise<BingoCell> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<BingoCell>(
    `${BINGO_API_BASE}/bingo-cards/${cardId}/cells/${cellId}/complete${query}`,
    { method: 'POST' }
  );
}

/**
 * List completed patterns for a card
 */
export async function listBingoPatterns(
  cardId: string,
  patternType?: string,
  patternIndex?: number
): Promise<BingoPatternListResponse> {
  const queryParams: Record<string, string | number> = {
    individual_id: getIndividualId(),
  };
  if (patternType) queryParams.patternType = patternType;
  if (patternIndex !== undefined) queryParams.patternIndex = patternIndex;

  const query = buildQueryString(queryParams);
  return fetchApi<BingoPatternListResponse>(
    `${BINGO_API_BASE}/bingo-cards/${cardId}/patterns${query}`
  );
}

/**
 * Get progress statistics for a card
 */
export async function getBingoProgress(cardId: string): Promise<BingoProgress> {
  const query = buildQueryString({ individual_id: getIndividualId() });
  return fetchApi<BingoProgress>(`${BINGO_API_BASE}/bingo-cards/${cardId}/progress${query}`);
}

